import { Injectable, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap, tap } from 'rxjs';
import Swal from 'sweetalert2';
import { UserListApiService } from './userlist-api.service';
import { AdminUser, AdminUsersResponse, UserBusqueda, UserFilter, UserListFiltros } from '../models/userlist.model';

const FILTROS_INICIALES: UserListFiltros = { filter: 'all', page: 0, size: 50 };
const DATOS_VACIOS: AdminUsersResponse = {
  totalCount: 0, verifiedCount: 0, unverifiedCount: 0, page: 0, size: 50, totalPages: 0, users: []
};

@Injectable({ providedIn: 'root' })
export class UserListStore {
  private readonly api = inject(UserListApiService);

  private readonly _filtros = signal<UserListFiltros>(FILTROS_INICIALES);
  private readonly _datos = signal<AdminUsersResponse>(DATOS_VACIOS);
  private readonly _cargando = signal(false);
  private readonly _exportando = signal(false);
  private readonly refreshTrigger = new Subject<void>();

  readonly filtros = this._filtros.asReadonly();
  readonly datos = this._datos.asReadonly();
  readonly cargando = this._cargando.asReadonly();
  readonly exportando = this._exportando.asReadonly();

  constructor() {
    toSignal(
      this.refreshTrigger.pipe(
        switchMap(() => {
          this._cargando.set(true);
          const f = this._filtros();
          return this.api.getUsers(f.filter, f.page, f.size, { dni: f.dni, email: f.email }).pipe(
            tap(res => { this._datos.set(res); this._cargando.set(false); }),
            catchError(err => {
              this._cargando.set(false);
              this._datos.set(DATOS_VACIOS);
              this.mostrarError(err);
              return of(null);
            })
          );
        })
      ),
      { initialValue: null }
    );
  }

  cargar(): void { this.refreshTrigger.next(); }

  reiniciar(): void {
    this._filtros.set(FILTROS_INICIALES);
    this.cargar();
  }

  cambiarFiltro(filter: UserFilter): void {
    this._filtros.update(f => ({ ...f, filter, page: 0 }));
    this.cargar();
  }

  // Búsqueda dentro del segmento activo; campos vacíos = sin filtro adicional.
  buscar(busqueda: UserBusqueda): void {
    const dni = busqueda.dni?.trim() || undefined;
    const email = busqueda.email?.trim() || undefined;
    this._filtros.update(f => ({ ...f, dni, email, page: 0 }));
    this.cargar();
  }

  limpiarBusqueda(): void {
    this.buscar({});
  }

  cambiarPagina(page: number): void {
    this._filtros.update(f => ({ ...f, page }));
    this.cargar();
  }

  cambiarTamanoPagina(size: number): void {
    this._filtros.update(f => ({ ...f, size, page: 0 }));
    this.cargar();
  }

  // Descarga disparada desde el store (no desde el componente), igual que el
  // resto de las features nuevas — ver convenciones-angular.md.
  exportar(): void {
    this._exportando.set(true);
    const f = this._filtros();
    this.api.exportUsers(f.filter, { dni: f.dni, email: f.email }).pipe(
      catchError(err => {
        this._exportando.set(false);
        this.mostrarError(err);
        return of(null);
      })
    ).subscribe(users => {
      this._exportando.set(false);
      if (users) this.descargarExcel(users);
    });
  }

  private async descargarExcel(users: AdminUser[]): Promise<void> {
    const XLSX = await import('xlsx');

    const rows = users.map(user => ({
      'Tipo Documento': user.tipoDocumento,
      'Nro Documento': user.nroDocumento,
      'Nombre': user.nombre,
      'Apellido': user.apellido,
      'Email': user.email,
      'Teléfono': user.telefono,
      'Estado': user.verificado ? 'Verificado' : 'No verificado',
      'Fecha de creación': user.fechaCreacion
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

    const fileName = `usuarios_${this._filtros().filter}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }

  private mostrarError(err: { status?: number; error?: { mensaje?: string } }): void {
    // 401: el authInterceptor ya hace logout() + navigate(['/login']).
    if (err.status === 401) return;
    const mensaje = err.status === 400
      ? 'Los parámetros de búsqueda son inválidos.'
      : 'Ha ocurrido un error. Por favor, inténtelo nuevamente.';
    Swal.fire({
      title: 'Error',
      text: err.error?.mensaje ?? mensaje,
      icon: 'error', confirmButtonText: 'Aceptar', confirmButtonColor: '#d33'
    });
  }
}
